import { ROUTES } from "Constants/AllRoutes";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge, Spin, Result, Button } from "antd";
import { ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getBlogsByCategory } from "./apis/blogs";
import { useNavigate, useParams } from "react-router-dom";
import { Flex } from "antd";
import { StyledButton } from "Components/Button/StyledButton";
// import placeholder from "Assets/placeholder.svg";
import "./styles/blogstyles.css";

// const videos = [
//   {
//     id: 1,
//     title: "My Journey from India to MIT",
//     description:
//       "Rahul shares his complete journey from preparation to admission",
//     thumbnail: placeholder,
//     duration: "12:45",
//     views: "45K",
//   },
//   {
//     id: 2,
//     title: "F-1 Visa Interview: Do's and Don'ts",
//     description: "Expert tips for acing your student visa interview",
//     thumbnail: placeholder,
//     duration: "8:30",
//     views: "32K",
//   },
//   {
//     id: 3,
//     title: "Budgeting as an International Student",
//     description: "Practical money management tips for students",
//     thumbnail: placeholder,
//     duration: "15:20",
//     views: "28K",
//   },
//   {
//     id: 4,
//     title: "Networking Tips for Career Success",
//     description: "How to build professional connections in the US",
//     thumbnail: placeholder,
//     duration: "10:15",
//     views: "19K",
//   },
// ];

const categories = [
  {
    title: "Career",
    description: "Learn about growing your career",
  },
  {
    title: "College",
    description: "Learn about your college options",
  },
  {
    title: "Finance",
    description: "Learn about your financial options",
  },
  {
    title: "Immigration",
    description: "Learn about immigration and visa options",
  },
];

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("all");
  const { category } = useParams();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const { data, isLoading, error } = useQuery({
    queryKey: ["blogs", activeCategory],
    queryFn: () => getBlogsByCategory(activeCategory),
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (data?.blogs) {
      setBlogs(data?.blogs);
    }
  }, [data]);

  useEffect(() => {
    if (category) {
      setActiveCategory(category);
    }
  }, [category]);

  const scrollToContent = () => {
    document
      .getElementById("blog-content")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  if (error) {
    return (
      <Result status="500" title="Error" subTitle="Please try again later" />
    );
  }

  return (
    <div className="h-full bg-white w-full">
      {/* Hero Section */}
      <section className="relative h-1/2 overflow-hidden bg-gradient-to-br from-white via-green-50/30 to-white py-2 md:py-24">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(128,226,128,0.1)_25%,rgba(128,226,128,0.1)_50%,transparent_50%,transparent_75%,rgba(128,226,128,0.1)_75%)] bg-[length:20px_20px] h-full" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            OneGrad Blog
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-4 md:mb-8 max-w-3xl mx-auto leading-relaxed">
            Expert guidance on Colleges, Finance, Immigration & Career—tailored
            for students.
          </p>
          <StyledButton
            onClick={scrollToContent}
            className="w-1/3 bg-[#80E280] hover:bg-[#70D270] text-white px-8 py-3 text-base md:text-lg font-medium rounded-lg transition-all duration-300 hover:scale-105 m-0"
          >
            Explore Latest Posts
          </StyledButton>
        </motion.div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-0 bg-white backdrop-blur-sm z-40 mx-auto category-tabs">
        <div className="mx-auto px-4 z-40 w-[90%]">
          <nav className="flex space-x-8 py-4 overflow-x-auto">
            {categories.map((category) => (
              <Button
                key={category.title}
                onClick={() => {
                  setActiveCategory(category.title.toLowerCase());
                }}
                className={`whitespace-nowrap px-4 py-2 text-medium font-medium rounded-full transition-all duration-300 ${
                  activeCategory === category.title.toLowerCase()
                    ? "bg-[#80E280] text-white shadow-lg"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                {category.title}
              </Button>
            ))}
          </nav>
        </div>
      </section>

      {/* Blog Grid */}
      {!isLoading ? (
        <section
          id="blog-content"
          className="py-10 md:py-16 px-4 z-10 mx-auto w-[90%]"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {blogs?.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-gray-200 border-solid"
                >
                  <div className="relative overflow-hidden">
                    {post?.imageUrl && (
                      <img
                        src={post.imageUrl}
                        alt={post.title}
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-[#80E280] text-white hover:bg-[#70D270] rounded-full px-2 py-1 z-10 text-sm font-bold">
                        {post.category.toUpperCase()}
                      </Badge>
                    </div>
                    {post.isFeatured && (
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-yellow-100 text-yellow-800 rounded-full px-2 py-1 text-sm font-bold">
                          Featured
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#80E280] transition-colors">
                      {post.title}
                    </h3>

                    <p className="text-gray-600 text-base mb-4 line-clamp-2">
                      {post.intro}
                    </p>

                    <Button
                      onClick={() => {
                        navigate(
                          ROUTES.SINGLE_BLOG.replace(":blogId", post.id),
                        );
                      }}
                      className="text-[#80E280] font-medium hover:text-[#70D270] transition-colors flex items-center group"
                    >
                      Read More
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </AnimatePresence>
        </section>
      ) : (
        <Flex justify="center" align="center" style={{ height: "100%" }}>
          <Spin size="large" />
        </Flex>
      )}

      {/* Featured Videos Section */}
      {/* <section className="py-10 md:py-16 bg-gray-50">
        <div className="m-4 mx-auto px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              🎥 Student Stories & Tips
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Watch advice, guides, and real student experiences.
            </p>
          </div>

          <div className="overflow-x-auto flex justify-center">
            <div
              className="flex justify-center space-x-6 pb-4"
              style={{ width: "max-content" }}
            >
              {videos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 w-80 flex-shrink-0"
                >
                  <div className="relative">
                    <Image
                      src={video.thumbnail || "/placeholder.svg"}
                      width={320}
                      height={180}
                      alt={video.title}
                      className="w-full h-40 object-cover"
                      preview={false}
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-3 group-hover:scale-110 transition-transform">
                        <Play className="h-6 w-6 text-gray-900 ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                      {video.duration}
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                      {video.description}
                    </p>
                    <p className="text-gray-500 text-xs">{video.views} views</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Email Subscription */}
      {/* <section className="p-10 md:p-16 bg-gradient-to-r from-green-50 to-green-100 ">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Stay Informed
          </h2>
          <p className="text-gray-600 mb-8 text-center text-lg">
            No spam. Just high-quality tips and stories from students like you.
          </p>

          <div className="flex flex-col justify-center items-center sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border-gray-300 focus:border-[#80E280] focus:ring-[#80E280]"
            />
            <StyledButton
              onClick={() => {}}
              className="px-8 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105"
            >
              Subscribe
            </StyledButton>
          </div>
        </motion.div>
      </section> */}
    </div>
  );
}
